import type { UiState, ViewSDK } from '@salesforce/platform-sdk/view';

import { getViewSDK } from '@salesforce/platform-sdk/view';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router';

import { queryClient } from './api/queryClient';
import { Layout } from './app/layouts/Layout';
import { ContractLineItemEditorPage } from './pages/contracts/pages/ContractLineItemEditorPage';
import { ContractsHomePage } from './pages/contracts/pages/ContractsHomePage';
import { QualityFormulasPage } from './pages/quality-formulas/pages/QualityFormulasPage';
import { TemplateCreateRouteGuard } from './pages/templates/components/TemplateCreateRouteGuard';
import { TemplateEditorPage } from './pages/templates/pages/TemplateEditorPage';
import { TemplateManagerPage } from './pages/templates/pages/TemplateManagerPage';
import { NoMatchPage } from './shared/pages/NoMatchPage';
import { useAccountStore } from './shared/stores/accountStore';
import { ToastViewport } from './shared/utils/notifications';
import { ViewSdkContext } from './ViewSdkContext';

interface ContractEntryUiState extends UiState {
  props: { accountId?: string; accountName?: string; availableEmbedHeight?: number };
}

export const App = () => {
  const [viewSdk, setViewSdk] = useState<ViewSDK>();
  const [availableEmbedHeight, setAvailableEmbedHeight] = useState<number>();
  const setAccountId = useAccountStore(state => state.setAccountId);
  const setAccountName = useAccountStore(state => state.setAccountName);

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void getViewSDK().then(sdk => {
      if (cancelled) return;
      const uiState = sdk.getUiState?.<ContractEntryUiState>();
      // If absent, start a new contract with no preselected Account.
      setAccountId(uiState?.state.props.accountId);
      // The host resolves this via lightning/uiRecordApi under the running user's own access, not a guest-side fetch.
      setAccountName(uiState?.state.props.accountName);
      setAvailableEmbedHeight(uiState?.state.props.availableEmbedHeight);
      unsubscribe = uiState?.subscribe(next => {
        setAccountId(next.props.accountId);
        setAccountName(next.props.accountName);
      });
      setViewSdk(sdk);
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [setAccountId, setAccountName]);

  useEffect(() => {
    if (!viewSdk) return;
    const handleHeightChange = (event: Event) => {
      const nextHeight = (event as CustomEvent<{ availableEmbedHeight: number }>).detail.availableEmbedHeight;
      if (typeof nextHeight === 'number' && Number.isFinite(nextHeight) && nextHeight >= 0) {
        setAvailableEmbedHeight(nextHeight);
      }
    };
    viewSdk.addEventListener?.('viewport-height-change', handleHeightChange);

    viewSdk.dispatchEvent?.(new CustomEvent('app-ready'));
    return () => viewSdk.removeEventListener?.('viewport-height-change', handleHeightChange);
  }, [viewSdk]);

  if (!viewSdk) {
    return null;
  }

  return (
    <ViewSdkContext value={viewSdk}>
      <QueryClientProvider client={queryClient}>
        <ToastViewport />
        <BrowserRouter>
          <Routes>
            <Route element={<Layout availableEmbedHeight={availableEmbedHeight} />} path="/">
              <Route element={<ContractsHomePage />} path="/" />
              <Route element={<ContractLineItemEditorPage />} path="contracts/:contractId/*" />
              {/* Quality formulas and templates remain separate feature routes. */}
              <Route element={<QualityFormulasPage />} path="quality-formulas" />
              <Route element={<TemplateManagerPage />} path="templates" />
              <Route element={<TemplateEditorPage />} path="templates/:id/edit" />
              <Route element={<TemplateCreateRouteGuard />}>
                <Route element={<TemplateEditorPage />} path="templates/new" />
              </Route>
              {/* Catch-all */}
              <Route element={<NoMatchPage />} path="*" />
            </Route>
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </ViewSdkContext>
  );
};
